import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../utils/seo';

export default function PrivacyPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const scrollToTop = () => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error scrolling to top:', error);
      window.scrollTo(0, 0);
    }
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      try {
        setIsScrolled(window.scrollY > 50);
        setShowScrollTop(window.scrollY > 300);
      } catch (error) {
        console.error('Error handling scroll:', error);
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  return (
    <>
      <SEO
        title="Privacy Policy | Community Iftar Project"
        description="Privacy Policy for the Community Iftar Project. Learn how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, Community Iftar, BMCS, UCT"
        canonical={`${siteUrl}/privacy`}
        ogTitle="Privacy Policy | Community Iftar Project"
        ogDescription="Privacy Policy for the Community Iftar Project. Learn how we collect, use, and protect your personal information."
        ogType="website"
        ogUrl={`${siteUrl}/privacy`}
      />
      <div className="relative min-h-screen bg-[#0F1829] overflow-x-hidden">
        {/* Fixed Background Elements */}
        <div className="fixed inset-0 z-0 pointer-events-none">
          <div className="absolute inset-0 opacity-20">
            <img 
              src="https://www.rahmaniyyah.com/calligraphy-background-stroke.png"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute bottom-[-15%] left-[-10%] w-[55%] h-[70%] bg-[#1a4d5c]/20 blur-[80px] opacity-20"></div>
            <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[60%] bg-[#2d5a6e]/20 blur-[90px] opacity-15"></div>
          </div>
        </div>

        {/* Top Navigation */}
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
          <nav className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-16 sm:h-20">
              {/* Logo */}
              <Link to="/" className="flex-shrink-0">
                <img 
                  src={isScrolled ? "https://static.readdy.ai/image/63f023a6a974325a1bd72d45110abc6b/49f4a0d491d56ed3fdb8c130286fff03.png" : "https://static.readdy.ai/image/63f023a6a974325a1bd72d45110abc6b/3f3a797d244b54989e1375000264c171.png"}
                  alt="Al-Madrasatu Ar-Raḥmāniyyah"
                  className="h-12 sm:h-14"
                />
              </Link>

              {/* Back to Home */}
              <Link 
                to="/"
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#0F1829] hover:bg-[#0F1829]/5' : 'text-white hover:bg-white/10'}`}
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Home
              </Link>
            </div>
          </nav>
        </header>

        <div className="relative z-10">
          {/* Hero Section */}
          <section className="relative min-h-[40vh] flex flex-col items-center justify-center px-4 sm:px-6 pt-32 pb-16 overflow-hidden">
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
              <img 
                src="https://static.readdy.ai/image/63f023a6a974325a1bd72d45110abc6b/53cf8a3331afd1a9812cee4e728aad75.png"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>

            <div className="relative z-10 w-full max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white mb-4">
                Privacy Policy
              </h1>
              <p className="text-lg sm:text-xl text-slate-200 font-medium">
                Last updated: January 2026
              </p>
            </div>
          </section>

          {/* Content Section */}
          <section className="relative py-16 sm:py-20 px-4 sm:px-6 overflow-hidden bg-white">
            <div className="relative z-10 max-w-4xl mx-auto">
              <div className="space-y-8">
                {/* Introduction */}
                <div className="relative rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl"></div>
                  <div className="relative p-6 sm:p-8">
                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                      The Community Iftar Project is a joint initiative of Bristol Muslim Cultural Society (BMCS) and Ummah Community Trust (UCT). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you interact with our project.
                    </p>
                  </div>
                </div>

                {/* Information We Collect */}
                <div className="relative rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl"></div>
                  <div className="relative p-6 sm:p-8">
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0F1829] mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#ED8C44]/20 flex items-center justify-center shrink-0">
                        <i className="ri-file-list-line text-[#ED8C44] text-xl"></i>
                      </div>
                      Information We Collect
                    </h2>
                    <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                      <p>When you register your interest to host a Community Iftar event, we may collect:</p>
                      <ul className="space-y-2 ml-6">
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span><strong className="text-[#0F1829]">Contact Information:</strong> Name, email address, phone number, and school/organization name</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span><strong className="text-[#0F1829]">Event Details:</strong> Proposed event dates, expected attendance, and venue information</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span><strong className="text-[#0F1829]">Grant Information:</strong> Financial details if applying for funding support</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span><strong className="text-[#0F1829]">Technical Data:</strong> IP address, browser type, and device information for website analytics</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* How We Use Your Information */}
                <div className="relative rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl"></div>
                  <div className="relative p-6 sm:p-8">
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0F1829] mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#6ba3b8]/20 flex items-center justify-center shrink-0">
                        <i className="ri-settings-3-line text-[#6ba3b8] text-xl"></i>
                      </div>
                      How We Use Your Information
                    </h2>
                    <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                      <p>We use the information we collect to:</p>
                      <ul className="space-y-2 ml-6">
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span>Process your registration and coordinate Community Iftar events</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span>Provide planning resources, support, and guidance</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span>Administer financial grants and monitor grant usage</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span>Send important updates about workshops, deadlines, and project information</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span>Improve our services and website functionality</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span>Comply with legal obligations and reporting requirements</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Data Protection */}
                <div className="relative rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl"></div>
                  <div className="relative p-6 sm:p-8">
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0F1829] mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#ED8C44]/20 flex items-center justify-center shrink-0">
                        <i className="ri-shield-check-line text-[#ED8C44] text-xl"></i>
                      </div>
                      Data Protection &amp; Security
                    </h2>
                    <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                      <p>
                        We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Your data is stored securely and accessed only by authorized personnel who need it to perform their duties.
                      </p>
                      <p>
                        We use secure third-party services (such as Tally for form submissions) that comply with data protection regulations. We do not sell, trade, or rent your personal information to third parties.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Data Retention */}
                <div className="relative rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl"></div>
                  <div className="relative p-6 sm:p-8">
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0F1829] mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#6ba3b8]/20 flex items-center justify-center shrink-0">
                        <i className="ri-time-line text-[#6ba3b8] text-xl"></i>
                      </div>
                      Data Retention
                    </h2>
                    <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                      <p>
                        We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. Event-related data is typically retained for up to 3 years for reporting and evaluation purposes.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Your Rights */}
                <div className="relative rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl"></div>
                  <div className="relative p-6 sm:p-8">
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0F1829] mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#ED8C44]/20 flex items-center justify-center shrink-0">
                        <i className="ri-user-settings-line text-[#ED8C44] text-xl"></i>
                      </div>
                      Your Rights
                    </h2>
                    <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                      <p>Under UK data protection law (GDPR), you have the right to:</p>
                      <ul className="space-y-2 ml-6">
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span><strong className="text-[#0F1829]">Access:</strong> Request a copy of the personal information we hold about you</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span><strong className="text-[#0F1829]">Correction:</strong> Request correction of inaccurate or incomplete information</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span><strong className="text-[#0F1829]">Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span><strong className="text-[#0F1829]">Restriction:</strong> Request restriction of processing in certain circumstances</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span><strong className="text-[#0F1829]">Objection:</strong> Object to processing of your personal information</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                          <span><strong className="text-[#0F1829]">Portability:</strong> Request transfer of your data to another organization</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Cookies */}
                <div className="relative rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl"></div>
                  <div className="relative p-6 sm:p-8">
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0F1829] mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#6ba3b8]/20 flex items-center justify-center shrink-0">
                        <i className="ri-cookie-line text-[#6ba3b8] text-xl"></i>
                      </div>
                      Cookies &amp; Tracking
                    </h2>
                    <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                      <p>
                        Our website uses cookies and similar technologies to enhance your browsing experience and analyze website traffic. Cookies are small text files stored on your device. You can control cookie settings through your browser preferences.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Third-Party Services */}
                <div className="relative rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl"></div>
                  <div className="relative p-6 sm:p-8">
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0F1829] mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#ED8C44]/20 flex items-center justify-center shrink-0">
                        <i className="ri-links-line text-[#ED8C44] text-xl"></i>
                      </div>
                      Third-Party Services
                    </h2>
                    <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                      <p>
                        We use Tally.so for form submissions. When you submit a registration form, your information is processed according to Tally's privacy policy. We recommend reviewing their privacy practices at <a href="https://tally.so/help/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#ED8C44] hover:underline">tally.so/help/privacy-policy</a>.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Changes to Policy */}
                <div className="relative rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl"></div>
                  <div className="relative p-6 sm:p-8">
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0F1829] mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#6ba3b8]/20 flex items-center justify-center shrink-0">
                        <i className="ri-refresh-line text-[#6ba3b8] text-xl"></i>
                      </div>
                      Changes to This Policy
                    </h2>
                    <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                      <p>
                        We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any significant changes by posting the updated policy on this page with a new "Last updated" date.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contact */}
                <div className="relative rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl"></div>
                  <div className="relative p-6 sm:p-8">
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0F1829] mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#ED8C44]/20 flex items-center justify-center shrink-0">
                        <i className="ri-mail-line text-[#ED8C44] text-xl"></i>
                      </div>
                      Contact Us
                    </h2>
                    <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                      <p>
                        If you have any questions about this Privacy Policy or wish to exercise your data protection rights, please contact us:
                      </p>
                      <div className="p-4 rounded-xl bg-[#0F1829]/[0.02] border border-[#0F1829]/5">
                        <p className="font-bold text-[#0F1829] mb-2">Community Iftar Project</p>
                        <p>Bristol Muslim Cultural Society (BMCS)</p>
                        <p>Ummah Community Trust (UCT)</p>
                        <p className="mt-2">Bristol, United Kingdom</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="relative py-8 sm:py-12 px-4 sm:px-6 bg-[#62C5C0]">
            <div className="max-w-6xl mx-auto">
              <div className="relative rounded-2xl overflow-hidden shadow-lg">
                <div className="absolute inset-0 border border-white/10 bg-white/[0.02] rounded-2xl opacity-75"></div>
                <div className="relative z-10 px-6 sm:px-10 py-8 sm:py-10">
                  <div className="flex flex-col items-center gap-6">
                    <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-6">
                      <Link to="/" className="flex items-center">
                        <img 
                          src="https://static.readdy.ai/image/63f023a6a974325a1bd72d45110abc6b/3f3a797d244b54989e1375000264c171.png"
                          alt="Al-Madrasatu Ar-Raḥmāniyyah"
                          className="opacity-80 hover:opacity-100 transition-opacity duration-300 h-16"
                        />
                      </Link>
                      <div className="flex items-center gap-4 text-base">
                        <Link to="/" className="text-white hover:text-white/80 transition-colors duration-200 whitespace-nowrap">
                          Home
                        </Link>
                        <span className="text-white/20">|</span>
                        <Link to="/privacy" className="text-white hover:text-white/80 transition-colors duration-200 whitespace-nowrap">
                          Privacy Policy
                        </Link>
                      </div>
                    </div>
                    <div className="w-full h-px bg-white/10"></div>
                    <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
                      <p className="text-white text-sm font-bold">
                        Project of Bristol Muslim Cultural Society (BMCS) and Ummah Community Trust (UCT)
                      </p>
                      <p className="text-white text-xs">
                        Powered by Proud Bristolians
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </footer>

          {/* Scroll to Top Button */}
          <button
            onClick={scrollToTop}
            className={`fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-[#ED8C44] hover:bg-[#ED8C44]/90 text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center cursor-pointer ${
              showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
            }`}
            aria-label="Scroll to top"
          >
            <i className="ri-arrow-up-line text-xl"></i>
          </button>
        </div>
      </div>
    </>
  );
}
